/**
 * Battle HUD widgets (owner: combat-builder). Parchment panels with lacquer borders (ART_DIRECTION
 * "UI" row), live Chinese text through the scene-local `battleText` (crisp at integer zoom), integer
 * positions only. Widgets are dumb: the scene feeds them engine state.
 */
import Phaser from 'phaser';
import { FONT_BODY, FONT_TITLE } from '@/core/config';
import { audio } from '@/audio';
import { wrapText, wrapToString } from '@/ui/wrap';
import { battleText } from './text';
import type { ActionView } from './engine';

export const HudColor = {
  parchment: 0xf1e3c2, parchmentDark: 0xd9c49a, lacquer: 0x8c2a22, lacquerDark: 0x4a1410, gold: 0xc9a54a,
  ink: 0x2a1d14, inkSoft: 0x5a4636, hp: 0x3c7a3a, hpMid: 0xffb347, hpLow: 0xb3453a, sp: 0x2b3a5c, track: 0xcdb88f, night: 0x1a120c,
} as const;
export const HudCss = { ink: '#2a1d14', inkSoft: '#5a4636', parchment: '#f1e3c2', sand: '#e2c48c', lacquer: '#8c2a22', dim: '#8a7a62', teal: '#1f8f8a', lantern: '#ffb347' } as const;

export const HUD_DEPTH = 3000;

/** Parchment panel with a lacquer border and an inner gold line. Returns the objects so callers can group them. */
export function drawPanel(scene: Phaser.Scene, x: number, y: number, w: number, h: number, alpha = 0.94): Phaser.GameObjects.GameObject[] {
  const fill = scene.add.rectangle(x, y, w, h, HudColor.parchment, alpha).setOrigin(0).setStrokeStyle(2, HudColor.lacquer);
  const inner = scene.add.rectangle(x + 3, y + 3, w - 6, h - 6).setOrigin(0).setStrokeStyle(1, HudColor.gold, 0.55);
  return [fill, inner];
}

// ---------------------------------------------------------------- Bar

export class Bar {
  readonly objects: Phaser.GameObjects.GameObject[];
  private readonly fill: Phaser.GameObjects.Rectangle;
  private readonly num: Phaser.GameObjects.Text;
  private readonly w: number;
  private shown = { v: 1 };
  private tween: Phaser.Tweens.Tween | null = null;

  constructor(private readonly scene: Phaser.Scene, x: number, y: number, w: number, h: number, label: string, private readonly color: number | ((ratio: number) => number), opts: { labelWidth?: number; numbers?: boolean } = {}) {
    const lw = opts.labelWidth ?? 30;
    this.w = w;
    const objs: Phaser.GameObjects.GameObject[] = [];
    if (label) objs.push(battleText(scene, x, y + h / 2, label, { fontFamily: FONT_BODY, fontSize: '12px', color: HudCss.inkSoft }).setOrigin(0, 0.5));
    const bx = x + (label ? lw : 0);
    objs.push(scene.add.rectangle(bx, y, w, h, HudColor.track).setOrigin(0).setStrokeStyle(1, HudColor.lacquerDark, 0.6));
    this.fill = scene.add.rectangle(bx + 1, y + 1, w - 2, h - 2, typeof color === 'number' ? color : color(1)).setOrigin(0);
    objs.push(this.fill);
    this.num = battleText(scene, bx + w + 4, y + h / 2, '', { fontFamily: FONT_BODY, fontSize: '11px', color: HudCss.ink }).setOrigin(0, 0.5).setVisible(opts.numbers !== false);
    objs.push(this.num);
    this.objects = objs;
  }

  set(value: number, max: number, animate = true): void {
    const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
    this.num.setText(`${value}/${max}`);
    if (this.tween) { this.tween.stop(); this.tween = null; }
    const apply = () => {
      this.fill.width = Math.max(0, Math.round((this.w - 2) * this.shown.v));
      this.fill.setFillStyle(typeof this.color === 'number' ? this.color : this.color(this.shown.v));
    };
    if (!animate) { this.shown.v = ratio; apply(); return; }
    this.tween = this.scene.tweens.add({ targets: this.shown, v: ratio, duration: 320, ease: 'Cubic.easeOut', onUpdate: apply, onComplete: () => { this.tween = null; apply(); } });
  }
}

export function hpColor(ratio: number): number { return ratio > 0.5 ? HudColor.hp : ratio > 0.25 ? HudColor.hpMid : HudColor.hpLow; }

// ---------------------------------------------------------------- Turn queue (行动预告)

export interface QueueEntry { id: string; name: string; side: 'party' | 'enemy'; state: 'done' | 'current' | 'next' }

export class TurnQueue {
  readonly container: Phaser.GameObjects.Container;
  private chips: Phaser.GameObjects.GameObject[] = [];
  private readonly roundText: Phaser.GameObjects.Text;
  private readonly goalText: Phaser.GameObjects.Text;

  constructor(private readonly scene: Phaser.Scene, x: number, y: number, w: number, goal: string) {
    this.container = scene.add.container(0, 0).setDepth(HUD_DEPTH);
    this.container.add(drawPanel(scene, x, y, w, 24));
    this.roundText = battleText(scene, x + 10, y + 12, '', { fontFamily: FONT_BODY, fontSize: '13px', fontStyle: 'bold', color: HudCss.lacquer }).setOrigin(0, 0.5);
    this.container.add(this.roundText);
    this.container.add(battleText(scene, x + 118, y + 12, '行动预告', { fontFamily: FONT_BODY, fontSize: '12px', color: HudCss.inkSoft }).setOrigin(0, 0.5));
    this.goalText = battleText(scene, x + w - 10, y + 12, goal, { fontFamily: FONT_BODY, fontSize: '11px', color: HudCss.inkSoft }).setOrigin(1, 0.5);
    this.container.add(this.goalText);
    this.x0 = x + 176; this.y0 = y + 12;
  }
  private readonly x0: number;
  private readonly y0: number;

  /** Round 0 = the opening lines before the first round: shows 「准备」 instead of a counter. */
  setRound(round: number, max: number): void { this.roundText.setText(round > 0 ? `第 ${round} / ${max} 回合` : '准备'); }

  set(entries: QueueEntry[]): void {
    for (const c of this.chips) c.destroy();
    this.chips = [];
    let x = this.x0;
    entries.forEach((e, i) => {
      const w = 64;
      const fill = e.state === 'current' ? HudColor.lacquer : e.state === 'done' ? HudColor.parchmentDark : HudColor.parchment;
      const rect = this.scene.add.rectangle(x, this.y0, w, 16, fill).setOrigin(0, 0.5).setStrokeStyle(1, e.state === 'done' ? HudColor.track : HudColor.lacquerDark, 0.8);
      const color = e.state === 'current' ? HudCss.parchment : e.state === 'done' ? HudCss.dim : HudCss.ink;
      const t = battleText(this.scene, x + w / 2, this.y0, e.name, { fontFamily: FONT_BODY, fontSize: '11px', color }).setOrigin(0.5, 0.5);
      this.container.add([rect, t]);
      this.chips.push(rect, t);
      x += w + 4;
      if (i < entries.length - 1) {
        const arrow = battleText(this.scene, x + 4, this.y0, '▸', { fontFamily: FONT_BODY, fontSize: '11px', color: HudCss.inkSoft }).setOrigin(0.5, 0.5);
        this.container.add(arrow); this.chips.push(arrow); x += 14;
      }
    });
  }

  destroy(): void { this.container.destroy(); }
}

// ---------------------------------------------------------------- Battle log (战况)

export class BattleLog {
  readonly container: Phaser.GameObjects.Container;
  private readonly text: Phaser.GameObjects.Text;
  private readonly lines: string[] = [];
  private readonly measure: (s: string) => number;

  constructor(scene: Phaser.Scene, x: number, y: number, w: number, h: number, private readonly maxLines = 6, private readonly charsPerLine = 15) {
    this.container = scene.add.container(0, 0).setDepth(HUD_DEPTH);
    this.container.add(drawPanel(scene, x, y, w, h));
    this.container.add(battleText(scene, x + 8, y + 6, '战况', { fontFamily: FONT_BODY, fontSize: '12px', fontStyle: 'bold', color: HudCss.lacquer }));
    this.text = battleText(scene, x + 8, y + 24, '', { fontFamily: FONT_BODY, fontSize: '11px', color: HudCss.ink, lineSpacing: 3 });
    this.container.add(this.text);
    this.measure = (s: string) => {
      const ctx = this.text.context;
      if (ctx) return ctx.measureText(s).width;
      return s.length * 11;
    };
  }

  push(line: string): void {
    // CJK kinsoku wrapping to prevent closing punctuation at start of lines.
    const wrapped = wrapText(line, this.charsPerLine * 11, this.measure);
    for (const w of wrapped) this.lines.push(w);
    while (this.lines.length > this.maxLines) this.lines.shift();
    this.text.setText(this.lines);
  }

  destroy(): void { this.container.destroy(); }
}

// ---------------------------------------------------------------- Command menu

export interface CommandMenuOptions { x: number; y: number; w: number; h: number; onMove?: (index: number) => void }

interface Row { bar: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text; hint: Phaser.GameObjects.Text; zone: Phaser.GameObjects.Zone }

export class CommandMenu {
  readonly container: Phaser.GameObjects.Container;
  private rows: Row[] = [];
  private views: ActionView[] = [];
  private index = 0;
  private resolveChoice: ((id: string) => void) | null = null;
  private readonly desc: Phaser.GameObjects.Text;
  private readonly hintText: Phaser.GameObjects.Text;
  private readonly measureDesc: (s: string) => number;
  private readonly rowH = 17;

  constructor(private readonly scene: Phaser.Scene, private readonly opts: CommandMenuOptions) {
    this.container = scene.add.container(0, 0).setDepth(HUD_DEPTH + 1).setVisible(false);
    this.container.add(drawPanel(scene, opts.x, opts.y, opts.w, opts.h));
    this.desc = battleText(scene, opts.x + 10, opts.y + 10 + 4 * this.rowH, '', { fontFamily: FONT_BODY, fontSize: '11px', color: HudCss.inkSoft, lineSpacing: 2 });
    this.measureDesc = (s: string) => {
      const ctx = this.desc.context;
      if (ctx) return ctx.measureText(s).width;
      return s.length * 11;
    };
    this.hintText = battleText(scene, opts.x + 10, opts.y + opts.h - 14, '↑↓ 选择 · Enter 确认 · 也可点击', { fontFamily: FONT_BODY, fontSize: '10px', color: HudCss.dim });
    this.container.add([this.desc, this.hintText]);
  }

  isOpen(): boolean { return this.resolveChoice !== null; }
  currentId(): string | null { return this.views[this.index]?.action.id ?? null; }

  /** Shows the commands and resolves with the chosen action id. Disabled rows are shown greyed with their reason. */
  open(views: ActionView[]): Promise<string> {
    this.close();
    this.views = views;
    this.build();
    this.container.setVisible(true);
    this.select(this.firstUsable(0, 1), false);
    return new Promise<string>((resolve) => { this.resolveChoice = resolve; });
  }

  /** Programmatic choice (touch buttons / debug hook). */
  choose(id: string): boolean {
    const i = this.views.findIndex((v) => v.action.id === id);
    if (i < 0 || !this.views[i]?.canUse) return false;
    this.select(i, false);
    this.confirm();
    return true;
  }

  move(dy: number): void {
    if (!this.isOpen() || this.views.length === 0) return;
    const next = this.firstUsable((this.index + dy + this.views.length) % this.views.length, dy < 0 ? -1 : 1);
    if (next !== this.index) { this.select(next); audio().sfx('ui-move', { volume: 0.5 }); }
  }

  confirm(): void {
    const v = this.views[this.index];
    if (!this.isOpen() || !v || !v.canUse) return;
    const resolve = this.resolveChoice;
    this.resolveChoice = null;
    audio().sfx('ui-confirm', { volume: 0.6 });
    this.container.setVisible(false);
    resolve?.(v.action.id);
  }

  close(): void {
    this.resolveChoice = null;
    this.container.setVisible(false);
  }

  destroy(): void { this.close(); this.container.destroy(); }

  private firstUsable(from: number, dir: 1 | -1): number {
    const n = this.views.length;
    for (let k = 0; k < n; k++) { const i = (from + k * dir + n * n) % n; if (this.views[i]?.canUse) return i; }
    return from;
  }

  private select(i: number, notify = true): void {
    this.index = i;
    this.rows.forEach((r, k) => {
      const v = this.views[k];
      const sel = k === i;
      r.bar.setVisible(sel);
      r.text.setColor(sel ? HudCss.parchment : v?.canUse ? HudCss.ink : HudCss.dim);
      r.hint.setColor(sel ? HudCss.sand : HudCss.dim);
    });
    const v = this.views[i];
    const rawDesc = v ? (v.canUse ? v.action.description : `${v.reason ?? '不可用'}。${v.action.description}`) : '';
    const wrappedDesc = rawDesc ? wrapToString(rawDesc, this.opts.w - 20, this.measureDesc) : '';
    this.desc.setText(wrappedDesc);
    if (notify) this.opts.onMove?.(i);
  }

  private build(): void {
    for (const r of this.rows) { r.bar.destroy(); r.text.destroy(); r.hint.destroy(); r.zone.destroy(); }
    this.rows = [];
    const { x, y, w } = this.opts;
    this.views.forEach((v, i) => {
      const ry = y + 8 + i * this.rowH;
      const bar = this.scene.add.rectangle(x + 6, ry, w - 12, this.rowH - 1, HudColor.lacquer).setOrigin(0).setVisible(false);
      const text = battleText(this.scene, x + 14, ry + this.rowH / 2, v.action.name, { fontFamily: FONT_BODY, fontSize: '13px', color: v.canUse ? HudCss.ink : HudCss.dim }).setOrigin(0, 0.5);
      const hintStr = v.canUse ? (v.action.hint ?? '') : (v.reason ?? '');
      const hint = battleText(this.scene, x + w - 14, ry + this.rowH / 2, hintStr, { fontFamily: FONT_BODY, fontSize: '10px', color: HudCss.dim }).setOrigin(1, 0.5);
      const zone = this.scene.add.zone(x + 6, ry, w - 12, this.rowH).setOrigin(0).setInteractive({ useHandCursor: v.canUse });
      zone.on(Phaser.Input.Events.POINTER_OVER, () => { if (this.isOpen() && v.canUse && this.index !== i) { this.select(i); audio().sfx('ui-move', { volume: 0.5 }); } });
      zone.on(Phaser.Input.Events.POINTER_DOWN, (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: { stopPropagation?: () => void }) => {
        ev.stopPropagation?.();
        if (!this.isOpen() || !v.canUse) return;
        this.select(i, false);
        this.confirm();
      });
      this.container.add([bar, text, hint, zone]);
      this.rows.push({ bar, text, hint, zone });
    });
  }
}

// ---------------------------------------------------------------- Banner (round / intent / result)

export class Banner {
  readonly container: Phaser.GameObjects.Container;
  private readonly strip: Phaser.GameObjects.Rectangle;
  private readonly text: Phaser.GameObjects.Text;
  private readonly sub: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, private readonly cx: number, private readonly cy: number) {
    this.container = scene.add.container(0, 0).setDepth(HUD_DEPTH + 5).setVisible(false);
    this.strip = scene.add.rectangle(cx, cy, 240, 30, HudColor.night, 0.78).setOrigin(0.5).setStrokeStyle(1, HudColor.gold, 0.7);
    this.text = battleText(scene, cx, cy, '', { fontFamily: FONT_TITLE, fontSize: '18px', fontStyle: 'bold', color: HudCss.sand }).setOrigin(0.5);
    this.sub = battleText(scene, cx, cy + 22, '', { fontFamily: FONT_BODY, fontSize: '11px', color: HudCss.parchment, stroke: '#1a120c', strokeThickness: 3 }).setOrigin(0.5, 0);
    this.container.add([this.strip, this.text, this.sub]);
  }

  show(text: string, sub = ''): void {
    this.text.setText(text);
    this.sub.setText(sub).setVisible(sub.length > 0);
    this.strip.width = Math.max(160, Math.round(this.text.width) + 48);
    this.container.setVisible(true);
  }
  hide(): void { this.container.setVisible(false); }
  destroy(): void { this.container.destroy(); }
}
